// ABOUTME: Reusable bottom sheet component for mobile interactions
// ABOUTME: Supports half/full sizes, drag-to-dismiss, and animated transitions

import { AnimatePresence, motion } from 'framer-motion'
import type { ReactNode } from 'react'

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  size?: 'half' | 'full'
}

const sizeClasses = {
  half: 'max-h-[50vh]',
  full: 'max-h-[90vh]',
}

export default function BottomSheet({
  open,
  onClose,
  children,
  title,
  size = 'half',
}: BottomSheetProps) {
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <motion.div
            data-testid="bottom-sheet-overlay"
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="bottom-sheet"
            className={`absolute bottom-0 left-0 right-0 bg-surface rounded-t-2xl shadow-[0_-4px_24px_rgba(0,0,0,0.15)] ${sizeClasses[size]} flex flex-col safe-bottom`}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            onDragEnd={(_e, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) {
                onClose()
              }
            }}
          >
            <div className="flex justify-center pt-3 pb-2" data-testid="drag-handle">
              <div className="w-10 h-1 rounded-full bg-text-tertiary" />
            </div>

            {title && (
              <div className="px-4 pb-3 border-b border-fg-tertiary/20">
                <h2 className="text-lg font-semibold text-fg-primary">{title}</h2>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
